import { Button } from '@/components/ui/button'
import {
  Database,
  DollarSign,
  FileText,
  Gauge,
  MapPin,
  MoveRight,
  Package,
  Truck,
  Users,
} from 'lucide-react'

export const DataTypesSection = () => {
  return (
    <section className='relative z-20 py-32 bg-[#FAFAFA]'>
      <div className='container mx-auto px-8'>
        <div className='text-center'>
          <span className='mb-3 text-sm tracking-widest text-gray-500 md:text-base'>
            24+ INTEGRATED DATA TYPES
          </span>
          <h1 className='mt-4 text-4xl font-medium text-balance lg:text-6xl text-gray-900'>
            The complete fuel supply chain <br />
            in one universal data exchange.
          </h1>
          <p className='mx-auto mt-6 max-w-2xl text-lg text-gray-600'>
            Crossroads unifies the fragmented data landscape into a single
            source of truth for your entire operation. Every price, every
            transaction, every gallon.
          </p>
          <Button
            className='mt-8 bg-[#9687F5] hover:bg-[#8073e8] text-white'
            size='lg'
          >
            See All Data Types
            <MoveRight className='ml-2' strokeWidth={1} />
          </Button>
        </div>

        <div className='mt-16 grid gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 md:grid-cols-2 lg:grid-cols-4'>
          {/* Terminals */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <MapPin className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Terminals
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Access to over 1,400 terminals organized by TCN.
              </p>
            </div>
          </div>

          {/* Inventory */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <Gauge className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Inventory Readings
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Real-time tank readings from any ATG, any MNSP, in any format.
              </p>
            </div>
          </div>

          {/* Transportation */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <Truck className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Fleet & Drivers
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                All carriers organized by SCAC code. Largest database of fuel
                drivers with carding management.
              </p>
            </div>
          </div>

          {/* Products */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <Package className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Products & Blends
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Track base products, custom blends, and recipe specifications
                with full regulatory compliance.
              </p>
            </div>
          </div>
        </div>

        {/* Second row */}
        <div className='grid gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 md:grid-cols-2 lg:grid-cols-4 mt-px'>
          {/* Orders & Documents */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <FileText className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Orders & BOLs
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Digital order flow from request to delivery with automated BOL
                generation and proof of delivery.
              </p>
            </div>
          </div>

          {/* Pricing */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <DollarSign className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Pricing & Invoicing
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Live rack pricing, freight rates, and automated invoice
                reconciliation across all counterparties.
              </p>
            </div>
          </div>

          {/* Allocations */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <Database className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Allocations & Contracts
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Manage volume commitments, contract terms, and load
                authorizations in one unified system.
              </p>
            </div>
          </div>

          {/* Counterparties */}
          <div className='flex flex-col gap-3 bg-white p-5 md:gap-6'>
            <Users className='size-6 shrink-0 text-[#9687F5]' />
            <div>
              <h2 className='text-sm font-medium md:text-base text-gray-900'>
                Counterparties
              </h2>
              <p className='text-sm text-gray-600 md:text-base'>
                Central database with all North American refiners, shippers,
                wholesalers, retailers, and carriers.
              </p>
            </div>
          </div>
        </div>

        <div className='mt-12 text-center'>
          <p className='text-lg text-gray-600'>
            Spend less time chasing data and more time chasing margin.
          </p>
        </div>
      </div>
    </section>
  )
}
